import { Box, Button, Typography } from "@mui/material";
import { BreakpointBox, CircularLoading } from "../../../Atoms";
import { GenericInput, GenericSelect } from "../../../Molecules";
import { useBreakpoints } from "../../../../../hooks/useBreakpoins";
import useAuth from "../../../../../hooks/context/useAuth";
import { useTranslate } from "../../../../../hooks/useTranslate";
import * as yup from "yup";
import { countriesWithZones } from "./data";

import { useFormik } from "formik";

import { useEffect } from "react";
import { colors } from "../../../../../theme/colors";

interface Props {
  userData: User;
  submitLoading: boolean;
  address?: IAddress | null;
  handleSubmit(val: EditCreateAddress): void;
  thisUserAlreadyHaveAddress: boolean;
}

export function EditCreateAddress({
  userData,
  submitLoading,
  address,
  handleSubmit,
  thisUserAlreadyHaveAddress,
}: Props) {
  const { isMobile } = useBreakpoints();
  const { strings } = useTranslate();
  const { language } = useAuth();

  const validationSchema = yup.object({
    address: yup
      .string()
      .required(strings.validationFields.name.required[language]),
    adressNumber: yup
      .string()
      .required(strings.validationFields.addressNumber[language]),
    // addressComplement: yup
    //   .string()
    //   .required(strings.validationFields.addressComplement[language]),
    neighborhood: yup.string().required("Informe o bairro"),
    city: yup.string().required(strings.validationFields.city[language]),
    state: yup.string().required(strings.validationFields.state[language]),
    postalCode: yup
      .string()
      .required(strings.validationFields.zipCode[language]),
    country: yup.string().required(strings.validationFields.country[language]),
    ssn: yup.string().required("Informe o CPF"),
    receiver: yup
      .string()
      .required(strings.validationFields.name.required[language]),
  });

  const formik = useFormik({
    initialValues: {
      address: "",
      adressNumber: "",
      addressComplement: "",
      neighborhood: "",
      city: "",
      state: "",
      postalCode: "",
      country: "Brasil",
      ssn: "",
      receiver: "",
      globalZoneId: 0,
    },
    validationSchema,
    async onSubmit(values: any) {
      const globalZoneId = countriesWithZones.find(
        (country) => country.name === values.country
      )?.globalZoneId;

      if (address?.id) {
        handleSubmit({
          type: "update",
          addressID: address?.id,
          data: {
            ...values,
            globalZoneId,
          },
        });
      } else {
        handleSubmit({
          type: "create",
          userID: userData?.user?.id,
          data: { ...values, globalZoneId },
        });
      }
    },
  });

  useEffect(() => {
    if (address) {
      formik.setFieldValue("address", address.address);
      formik.setFieldValue("adressNumber", address.adressNumber);
      formik.setFieldValue("addressComplement", address.addressComplement);
      formik.setFieldValue("city", address.city);
      formik.setFieldValue("state", address.state);
      formik.setFieldValue("postalCode", address.postalCode);
      // formik.setFieldValue("country", address.country);
      formik.setFieldValue("ssn", address.ssn);
      formik.setFieldValue("receiver", address.receiver);
      formik.setFieldValue("globalZoneId", address.globalZoneId);
      formik.setFieldValue("neighborhood", address.neighborhood);
    }

    if (!thisUserAlreadyHaveAddress) {
      formik.setFieldValue("ssn", userData?.user?.ssn);
      formik.setFieldValue("receiver", userData?.user?.name);
    }
    formik.setFieldValue("country", "Brasil");
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ minWidth: "180px", p: 3 }}>
      <Typography fontSize={24} color={colors.purple[500]} fontWeight={700}>
        Novo endereço
      </Typography>
      {submitLoading ? (
        <CircularLoading />
      ) : (
        <>
          <BreakpointBox>
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.address && formik?.errors?.address
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.address ? formik?.errors?.address : ""
              }
              value={formik?.values?.address}
              placeholder={"Endereço"}
              inputName="address"
            />
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.adressNumber && formik?.errors?.adressNumber
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.adressNumber
                  ? formik?.errors?.adressNumber
                  : ""
              }
              value={formik?.values?.adressNumber}
              placeholder={"Número"}
              inputName="adressNumber"
              width={isMobile ? "100%" : "50%"}
            />
          </BreakpointBox>
          <BreakpointBox>
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.addressComplement &&
                formik?.errors?.addressComplement
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.addressComplement
                  ? formik?.errors?.addressComplement
                  : ""
              }
              value={formik?.values?.addressComplement}
              placeholder={"Complemento"}
              inputName="addressComplement"
            />
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.neighborhood && formik?.errors?.neighborhood
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.neighborhood
                  ? formik?.errors?.neighborhood
                  : ""
              }
              value={formik?.values?.neighborhood}
              placeholder={"Bairro"}
              inputName="neighborhood"
            />
          </BreakpointBox>
          <BreakpointBox>
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.city && formik?.errors?.city ? true : false
              }
              errorMessage={formik?.touched?.city ? formik?.errors?.city : ""}
              value={formik?.values?.city}
              placeholder={"Cidade"}
              inputName="city"
              width={isMobile ? "100%" : "50%"}
            />
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.state && formik?.errors?.state ? true : false
              }
              errorMessage={formik?.touched?.state ? formik?.errors?.state : ""}
              value={formik?.values?.state}
              placeholder={"Estado"}
              inputName="state"
              width={isMobile ? "100%" : "50%"}
            />
          </BreakpointBox>
          <BreakpointBox>
            <GenericSelect
              disabled
              name="country"
              onChange={(value, name) => formik.setFieldValue("country", value)}
              options={countriesWithZones.map((data) => ({
                label: data.name,
                value: data.name,
              }))}
              placeholder={"País"}
              value={formik?.values?.country}
              defaultValue={formik?.values?.country}
              error={
                formik?.touched?.country && formik?.errors?.country
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.country ? formik?.errors?.country : ""
              }
              width={isMobile ? "100%" : "50%"}
            />
            {/* <GenericInput
          hasIcon={false}
          onChange={(value, name) => formik.setFieldValue(name, value)}
          error={
            formik?.touched?.country && formik?.errors?.country ? true : false
          }
          errorMessage={formik?.touched?.country ? formik?.errors?.country : ""}
          value={formik?.values?.country}
          placeholder={strings.myAddress.labels.country[language]}
          inputName="country"
          width={isMobile ? "100%" : "50%"}
        /> */}
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.postalCode && formik?.errors?.postalCode
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.postalCode ? formik?.errors?.postalCode : ""
              }
              value={formik?.values?.postalCode}
              placeholder={"CEP"}
              inputName="postalCode"
              width={isMobile ? "100%" : "50%"}
            />
          </BreakpointBox>

          <BreakpointBox>
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={formik?.touched?.ssn && formik?.errors?.ssn ? true : false}
              errorMessage={formik?.touched?.ssn ? formik?.errors?.ssn : ""}
              value={formik?.values?.ssn}
              placeholder={"CPF"}
              inputName="ssn"
            />

            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                formik?.touched?.receiver && formik?.errors?.receiver
                  ? true
                  : false
              }
              errorMessage={
                formik?.touched?.receiver ? formik?.errors?.receiver : ""
              }
              value={formik?.values?.receiver}
              placeholder={"Destinatário"}
              inputName="receiver"
              width={"100%"}
            />
          </BreakpointBox>
          <Button
            sx={{ mt: 3 }}
            fullWidth
            variant="contained"
            onClick={() => formik.handleSubmit()}
          >
            {address?.id ? "Atualizar" : "Criar"}
          </Button>
        </>
      )}
    </Box>
  );
}
